import React from "react";
import { SectionContentProps } from "../../types";
import EditableText from "../../core/EditableText";
import { PiArrowRightBold } from "react-icons/pi";
import logger from "@/utils/logger";

export default function HokHero({
  content,
  onContentChange,
  isSelected,
}: SectionContentProps) {
  const primaryColor = content.colorPalette?.primary || "#684DF4"; // Purple Theme
  const secondaryColor = content.colorPalette?.secondary || "#8a2be2";
  const fontFamily = content.fontFamily || "'Inter', sans-serif";

  // Left Column Images List (Duplicated for seamless looping)
  const defaultLeftImages = [
    "/assets/img/hero/hero-img-1.png",
    "/assets/img/hero/hero-img-2.png",
    "/assets/img/hero/hero-img-3.png",
    "/assets/img/hero/hero-img-4.png",
    "/assets/img/hero/hero-img-5.png",
    "/assets/img/hero/hero-img-6.png",
  ];

  const leftColumnImages = content.leftColumnImages?.length
    ? [...content.leftColumnImages, ...content.leftColumnImages]
    : [...defaultLeftImages, ...defaultLeftImages];

  // Right Column Images List (Duplicated and reversed for seamless loop in opposite direction)
  const defaultRightImages = [
    "/assets/img/hero/hero-img-6.png",
    "/assets/img/hero/hero-img-5.png",
    "/assets/img/hero/hero-img-4.png",
    "/assets/img/hero/hero-img-3.png",
    "/assets/img/hero/hero-img-2.png",
    "/assets/img/hero/hero-img-1.png",
  ];

  const updateLeftImage = (idx: number, url: string) => {
    if (!onContentChange) return;
    const currentLeft = content.leftColumnImages || defaultLeftImages;
    const newLeft = [...currentLeft];
    const realIdx = idx % currentLeft.length;
    newLeft[realIdx] = url;
    onContentChange({ ...content, leftColumnImages: newLeft });
  };

  const updateRightImage = (idx: number, url: string) => {
    if (!onContentChange) return;
    const currentRight = content.rightColumnImages || defaultRightImages;
    const newRight = [...currentRight];
    const realIdx = idx % currentRight.length;
    newRight[realIdx] = url;
    onContentChange({ ...content, rightColumnImages: newRight });
  };

  const rightColumnImages = content.rightColumnImages?.length
    ? [...content.rightColumnImages, ...content.rightColumnImages]
    : [...defaultRightImages, ...defaultRightImages];

  const backgroundImage = content.backgroundImage || "/assets/img/bg/hero_bg_1.jpg";

  return (
    <>
      <div
        className={`relative overflow-hidden w-full bg-[#FAF9FF] pt-8 pb-8 lg:pt-12 lg:pb-10 ${isSelected ? "ring-2 ring-blue-500 relative z-20 shadow-md" : ""
          }`}
        style={{
          fontFamily,
          backgroundImage: `url(${backgroundImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        {/* Soft Radial Gradients on bottom-left and bottom-right ends */}
        <div className="absolute -bottom-20 -left-20 w-[450px] h-[450px] rounded-full bg-purple-500/10 blur-[100px] pointer-events-none z-0" />
        <div className="absolute -bottom-20 -right-20 w-[450px] h-[450px] rounded-full bg-indigo-500/15 blur-[100px] pointer-events-none z-0" />

        {/* Dotted Grid Pattern Overlay */}
        <div
          className="absolute inset-0 opacity-[0.18] pointer-events-none z-0"
          style={{
            backgroundImage: "radial-gradient(rgba(104, 77, 244, 0.5) 1.5px, transparent 1.5px)",
            backgroundSize: "24px 24px",
          }}
        />

        <div className="max-w-7xl mx-auto px-4 sm:px-8 relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          {/* Left Column: Editable Copy & Action Buttons */}
          <div className="lg:col-span-6 flex flex-col text-center lg:text-left lg:pt-20">
            {/* Subtitle tag */}
            <span className="inline-block self-center lg:self-start text-xs sm:text-sm font-bold tracking-wider text-purple-600 bg-purple-50 border border-purple-100 rounded-full px-4 py-1.5 mb-6 uppercase">
              <EditableText
                value={content.tag || "Trusted by 1850+ Educators Worldwide"}
                onChange={(val) =>
                  onContentChange && onContentChange({ ...content, tag: val })
                }
                as="span"
                placeholder="Tag"
              />
            </span>

            {/* Main title heading */}
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-gray-900 leading-tight mb-6 tracking-tight">
              <EditableText
                value={content.title || "Largest"}
                onChange={(val) =>
                  onContentChange && onContentChange({ ...content, title: val })
                }
                as="span"
                placeholder="Title First Word"
              />{" "}
              <span
                className="text-transparent bg-clip-text"
                style={{
                  backgroundImage: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`,
                }}
              >
                <EditableText
                  value={content.highlightText || "Education Platform"}
                  onChange={(val) =>
                    onContentChange && onContentChange({ ...content, highlightText: val })
                  }
                  as="span"
                  placeholder="Highlighted Text"
                />
              </span>{" "}
              <EditableText
                value={content.titleEnd || "for Online Learning"}
                onChange={(val) =>
                  onContentChange && onContentChange({ ...content, titleEnd: val })
                }
                as="span"
                placeholder="Title End"
              />
            </h1>

            {/* Description text */}
            <p className="text-base sm:text-lg text-gray-600 leading-relaxed mb-8 max-w-xl mx-auto lg:mx-0">
              <EditableText
                value={content.text || "Instacks is a powerful platform to Create and Share Online Courses"}
                onChange={(val) =>
                  onContentChange && onContentChange({ ...content, text: val })
                }
                as="span"
                placeholder="Description text"
              />
            </p>

            {/* Buttons Group */}
            <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4">
              <button
                className="w-full sm:w-auto rounded px-8 py-3 text-sm font-bold text-white shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all flex items-center justify-center gap-2"
                style={{
                  background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`,
                }}
              >
                <EditableText
                  value={content.primaryBtnText || "Explore Courses"}
                  onChange={(val) =>
                    onContentChange && onContentChange({ ...content, primaryBtnText: val })
                  }
                  as="span"
                  placeholder="Primary Button"
                />
                <PiArrowRightBold className="w-4 h-4 text-white" />
              </button>

              <button className="w-full sm:w-auto rounded px-8 py-3 text-sm font-bold text-gray-800 bg-transparent border border-gray-300 hover:bg-gray-50 hover:-translate-y-0.5 transition-all flex items-center justify-center gap-2">
                <EditableText
                  value={content.secondaryBtnText || "Become an Instructor"}
                  onChange={(val) =>
                    onContentChange && onContentChange({ ...content, secondaryBtnText: val })
                  }
                  as="span"
                  placeholder="Secondary Button"
                />
                <PiArrowRightBold className="w-4 h-4 text-gray-800" />
              </button>
            </div>
          </div>

          {/* Right Column: Dual Vertical Portfolio Marquee (Auto-scrolling showcase) */}
          <div className="lg:col-span-6 relative z-10 select-none flex items-center justify-center lg:h-[650px] xl:h-[720px]">
            {/* Clean Grid layout: scaled down rotated parent with horizontal shifting to prevent overlaps */}
            <div className="grid grid-cols-2 gap-4 sm:gap-6 transform rotate-[15deg] scale-[0.95] translate-x-[10%] -translate-y-[5%] max-w-[460px] sm:max-w-[500px] w-full mx-auto">
              {/* Left Scroll Column (scrolling UP) */}
              <div className="flex flex-col gap-4 sm:gap-6 animate-scroll-up-loop hover:[animation-play-state:paused]">
                {leftColumnImages.map((src, idx) => (
                  <div
                    key={`left-${idx}`}
                    className="relative rounded-xl overflow-hidden shadow-md border border-gray-100/50 bg-white transition-transform duration-300 hover:scale-[1.03] hover:shadow-lg cursor-pointer group/img"
                  >
                    <img
                      src={src}
                      alt={`Showcase screen ${idx + 1}`}
                      className="w-full object-cover"
                      loading="lazy"
                    />
                    {isSelected && (
                      <label className="absolute inset-0 z-10 flex items-center justify-center bg-black/60 cursor-pointer transition-all opacity-0 group-hover/img:opacity-100">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              const toast = (await import("react-hot-toast")).default;
                              const homepageApi = (await import("@/services/homepageApi")).default;
                              const toastId = toast.loading("Uploading showcase image...");
                              try {
                                const response = await homepageApi.uploadImage(file);
                                const url = response.data?.url || response.url;
                                if (url) {
                                  updateLeftImage(idx, url);
                                  toast.success("Showcase image updated!", { id: toastId });
                                } else {
                                  toast.error("Failed to upload.", { id: toastId });
                                }
                              } catch (err) {
                                toast.error("Upload failed", { id: toastId });
                                logger.error(err);
                              }
                            }
                          }}
                        />
                      </label>
                    )}
                  </div>
                ))}
              </div>

              {/* Right Scroll Column (scrolling DOWN, staggered downwards using mt-20) */}
              <div className="flex flex-col gap-4 sm:gap-6 animate-scroll-down-loop mt-20 hover:[animation-play-state:paused]">
                {rightColumnImages.map((src, idx) => (
                  <div
                    key={`right-${idx}`}
                    className="relative rounded-xl overflow-hidden shadow-md border border-gray-100/50 bg-white transition-transform duration-300 hover:scale-[1.03] hover:shadow-lg cursor-pointer group/img"
                  >
                    <img
                      src={src}
                      alt={`Showcase screen ${idx + 1}`}
                      className="w-full object-cover"
                      loading="lazy"
                    />
                    {isSelected && (
                      <label className="absolute inset-0 z-10 flex items-center justify-center bg-black/60 cursor-pointer transition-all opacity-0 group-hover/img:opacity-100">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              const toast = (await import("react-hot-toast")).default;
                              const homepageApi = (await import("@/services/homepageApi")).default;
                              const toastId = toast.loading("Uploading showcase image...");
                              try {
                                const response = await homepageApi.uploadImage(file);
                                const url = response.data?.url || response.url;
                                if (url) {
                                  updateRightImage(idx, url);
                                  toast.success("Showcase image updated!", { id: toastId });
                                } else {
                                  toast.error("Failed to upload.", { id: toastId });
                                }
                              } catch (err) {
                                toast.error("Upload failed", { id: toastId });
                                logger.error(err);
                              }
                            }
                          }}
                        />
                      </label>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
